import React from 'react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Check, X, Shield } from 'lucide-react';

// Friendly labels per role — these surface in the header pill so the user
// sees a recognizable title instead of the raw enum value.
const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  op: 'Operator',
  accounting: 'Accounting',
  driver: 'Driver',
  owner_operator: 'Owner-Op',
  mechanic: 'Mechanic',
  marketplace_customer: 'Marketplace',
  customer: 'Customer',
};

// Module access matrix per role. Used to render the "What you can access"
// popover so the user knows at a glance which sections are open to them
// — preventing "why can't I see Master Tables?" support tickets.
//
// Source of truth for these mappings is `MainLayout.getNavItems()` plus the
// `allowedRoles` arrays in `App.js`. Keep this list in sync when adding
// new modules.
const ACCESS_MATRIX = {
  super_admin: { has: ['Everything', 'Super Admin Panel'], lacks: [] },
  admin: {
    has: ['Dashboard', 'Messages', 'GPS', 'Gate Schedules', 'Broker TMS', 'FreightMargin AI',
          'Proof of Delivery', 'Accounting', 'Compliance', 'Equipment Issues', 'Users',
          'Terminal Integration', 'System Settings'],
    lacks: ['Super Admin Panel'],
  },
  dispatcher: {
    has: ['Dashboard', 'Messages', 'GPS', 'Gate Schedules', 'Broker TMS', 'FreightMargin AI',
          'Proof of Delivery', 'Equipment Issues'],
    lacks: ['Accounting', 'Compliance', 'Users', 'Terminal Integration', 'System Settings'],
  },
  op: {
    has: ['Dashboard', 'Messages', 'GPS', 'Gate Schedules', 'Broker TMS', 'FreightMargin AI',
          'Proof of Delivery', 'Equipment Issues'],
    lacks: ['Accounting', 'Compliance', 'Users', 'Terminal Integration', 'System Settings'],
  },
  accounting: {
    has: ['Dashboard', 'Messages', 'Accounting'],
    lacks: ['GPS', 'Gate Schedules', 'Broker TMS', 'Users', 'Settings'],
  },
  driver: {
    has: ['Driver View', 'Messages', 'Proof of Delivery'],
    lacks: ['Dashboard (full)', 'Master Tables', 'Settings'],
  },
  mechanic: {
    has: ['Mechanic Workbench', 'Equipment Issues'],
    lacks: ['Dashboard', 'Messages', 'Settings'],
  },
};

/**
 * Compact role pill that lives next to the user's name in the header.
 * Click → small popover lists which app sections the role grants access to,
 * which sections it lacks. Cheap user-education that prevents the
 * "I logged in but I can't find X" support tickets.
 */
export const RoleBadge = ({ role }) => {
  if (!role) return null;
  const label = ROLE_LABELS[role] || role;
  const matrix = ACCESS_MATRIX[role] || { has: [], lacks: [] };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-[10px] uppercase tracking-wide text-slate-300 hover:text-white transition-colors"
          title="Your role — click to see what you can access"
          data-testid="role-badge-trigger"
        >
          <Shield className="h-3 w-3 text-amber-400" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0 bg-slate-900 border-slate-700"
        data-testid="role-badge-popover"
      >
        <div className="px-3 py-2 border-b border-slate-800">
          <p className="text-sm font-medium text-white flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-amber-400" />
            Signed in as <span className="font-mono text-amber-300">{label}</span>
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            This role grants you access to the modules below.
          </p>
        </div>

        <div className="px-3 py-2 space-y-2 text-xs max-h-80 overflow-y-auto">
          {matrix.has.length > 0 && (
            <div>
              <p className="text-emerald-400 font-medium mb-1.5 flex items-center gap-1">
                <Check className="h-3 w-3" /> Available
              </p>
              <ul className="space-y-0.5 pl-4">
                {matrix.has.map((mod) => (
                  <li key={mod} className="text-slate-300">{mod}</li>
                ))}
              </ul>
            </div>
          )}
          {matrix.lacks.length > 0 && (
            <div>
              <p className="text-slate-500 font-medium mb-1.5 flex items-center gap-1">
                <X className="h-3 w-3" /> Not available to your role
              </p>
              <ul className="space-y-0.5 pl-4">
                {matrix.lacks.map((mod) => (
                  <li key={mod} className="text-slate-500">{mod}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-3 py-2 border-t border-slate-800">
          <p className="text-[10px] text-slate-500">
            Need access to something else? Ask your administrator to update your role.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Re-export labels for use elsewhere (e.g. /unauthorized page).
export { ROLE_LABELS };
